// Humanoid Lab — self-contained box3d runtime for the /humanoid-lab page.
//
// Follows the yard/playground singleton pattern (getXRuntime/advanceXPhysics,
// flat rt.buffer.transforms via TransformBuffer, SleepManager discipline,
// kinematic drag-hand via spherical joint + setKinematicTarget). No `three`
// import — humanoidVisual.ts + HumanoidLab.tsx own all three-side rendering.
//
// Active pose-hold / balance are OWNED by humanoidMotor.ts (edited in
// parallel elsewhere). This file calls them through exactly one wrapper,
// `updateHumanoidActiveControl`, so improvements to those functions land here
// automatically without touching this file's control-flow.
import {
  createBox3D,
  probeCapabilities,
  FixedStepper,
  TransformBuffer,
  SleepManager,
  type Box3D,
  type BodyContactLoad,
  type World,
  type BodyHandle,
  type JointHandle,
} from 'box3d-web';
import {
  createHumanoid,
  createHumanoidCrowd,
  rotateVectorByQuat,
  type Humanoid,
} from './humanoid';
import {
  HUMANOID_BONE_NAMES,
  HUMANOID_MASS_FRACTIONS,
  anatomicalFootHeelBiasM,
  type HumanoidBoneName,
  type JointLimitPreset,
} from './humanoidBones';
import type { HumanoidSpecInput } from './humanoidSpec';
import {
  STAND_POSE,
  applyGetUp,
  applyMuscleJointCommands,
  applyPoseHold,
  applyStandAssist,
  createGetUpState,
  createMotorCommandBuffer,
  createPoseHoldRampState,
  disableAllJointMotors,
  emptyMotorTelemetrySnapshot,
  readHumanoidMotorTelemetry,
  resetGetUpState,
  updatePoseHoldRamp,
  type GetUpState,
  type HumanoidMotorCommandBuffer,
  type HumanoidMotorTelemetrySnapshot,
  type PoseHoldRampState,
} from './humanoidMotor';
import {
  MUSCLE_GROUP_NAMES,
  createMuscleControllerState,
  footSolePitchRadFromForward,
  hashMuscleControllerState,
  queueMuscleOneStep,
  queueMuscleSingleSupportHold,
  resetMuscleController,
  setP1ATrialStyleKnobs,
  stepMuscleController,
  type MuscleControllerOutput,
  type MuscleControllerState,
  type MuscleGroupName,
  type MuscleObservation,
  type MusclePhase,
  type MusclePushDirection,
  type MuscleStepPhase,
  type MuscleSwingSide,
  type P1ASagittalPolicyOffsets,
  type P1ATrialStyleKnobs,
} from './humanoidMuscleController';
import {
  createFootContactTracker,
  type FootContactTracker,
} from './humanoidFootContactTracker';
import {
  LOCOMOTION_TRACE_MAX_SAMPLES,
  createLocomotionTraceBuffer,
  type LocomotionTraceBuffer,
} from './humanoidLocomotionTrace';

export type HumanoidScenario = 'drop' | 'stairs' | 'hit' | 'drag' | 'pile' | 'balance' | 'muscleBalance';

/** User-only muscle-balance push directions (not used by the automatic controller). */
export type MuscleBalancePushDirection = Exclude<MusclePushDirection, null>;
/** Physical lifecycle of the most recent explicit push. */
export type MusclePushLifecycle = 'idle' | 'pending' | 'recovered' | 'fallen';

export interface HumanoidFootContactLoads {
  left: BodyContactLoad;
  right: BodyContactLoad;
}

/** Additive telemetry for the muscleBalance scenario (E2E / HUD bridge). */
export interface HumanoidMuscleTelemetry {
  controllerActive: boolean;
  controlMode: 'muscle-inspired';
  phase: MusclePhase;
  peakActivation: number;
  meanActivation: number;
  fatigue: number;
  weakLeftLeg: boolean;
  centerOfMass: readonly [number, number, number];
  supportCenter: readonly [number, number, number];
  supportErrorM: number;
  pelvisTiltDeg: number;
  lastPushDirection: MusclePushDirection;
  /** Certified (MVP 2/4) vs interactive stress-boundary tier of last push. */
  lastPushTier: MusclePushTier | null;
  /** Impulse magnitude (N·s) of last user push; 0 if none. */
  lastPushImpulseNs: number;
  /** Measured outcome of the latest push (not a timer-only UI flag). */
  pushLifecycle: MusclePushLifecycle;
  pushElapsedS: number;
  pushPeakSupportErrorM: number;
  pushPeakTiltDeg: number;
  pushPeakDisplacementM: number;
  /** When true, primary push buttons fire stress-tier impulses. */
  pushStressMode: boolean;
  forbiddenAssistCount: number;
  /** Per-group peak activation snapshot for visualization (additive). */
  groupActivation: Readonly<Record<MuscleGroupName, number>>;
  /** Deterministic controller-state fingerprint (reset / determinism gates). */
  stateHash: string;
  /** Capture-step sub-phase (`none` when standing balance only). */
  stepPhase: MuscleStepPhase;
  swingSide: MuscleSwingSide | null;
  stepCount: number;
  stepTargetXM: number;
  stepTargetZM: number;
  stepTargetWorldXM: number;
  stepTargetWorldYM: number;
  stepTargetWorldZM: number;
  stepLandingErrorM: number;
  supportSpanBeforeM: number;
  supportSpanAfterM: number;
  prepareDurationS: number;
  comToStanceM: number;
  minComToStanceM: number;
  comLateralToStanceM: number;
  comSagittalToStanceM: number;
  minComLateralToStanceM: number;
  minComSagittalToStanceM: number;
  swingLiftM: number;
  prepareAborted: boolean;
  prepareStancePlantErrorM: number;
  minPrepareStancePlantErrorM: number;
  maxPrepareStancePlantErrorM: number;
  prepareReadyDwellS: number;
  maxPrepareReadyDwellS: number;
  leftFootContactPointCount: number;
  rightFootContactPointCount: number;
  leftFootNormalImpulse: number;
  rightFootNormalImpulse: number;
  combinedFootNormalImpulse: number;
  leftFootLoadFraction: number;
  rightFootLoadFraction: number;
  stepArmed: boolean;
  plantedPoseActive: boolean;
  recoveredDwellS: number;
  integralErrorXM: number;
  integralErrorZM: number;
  integralCorrectionXM: number;
  integralCorrectionZM: number;
}

const FIXED_DT = 1 / 60;
// Matches humanoidEvaluation.ts's tuning (8, not 4): a hard single-limb ground
// impact transiently separates a joint's anchor pair further at fewer
// substeps. Re-derive the same value here rather than drift from the
// evaluated configuration.
const SUBSTEPS = 8;
const MAX_STEPS_PER_FRAME = 3;

const SETTLE_LIN_MPS = 0.05;
const SETTLE_ANG_RAD_S = 0.1;
const SETTLE_SUSTAIN_STEPS = 12;

const DEFAULT_HEIGHT_M = 1.8;
const DEFAULT_MASS_KG = 75;

const DROP_HEIGHT_M = 2;

const PILE_COUNT = 10;
const PILE_DROP_HEIGHT_M = 3;
const PILE_SPACING_M = 1.4;

const STAIR_COUNT = 5;
const STAIR_RISE_M = 0.22;
const STAIR_RUN_M = 0.32;
const STAIR_WIDTH_M = 1.6;

// buildStairs lays step `i`'s center at Z = -i * STAIR_RUN_M with top height
// (i+1) * STAIR_RISE_M — so the TALLEST step sits at the most-negative Z and
// height falls off toward Z=0 and beyond (the general ground plane past step
// 0). The staircase therefore descends toward +Z, not -Z. Spawn tuning below
// leans/shoves the rig in +Z so it actually cascades down the risers instead
// of toppling toward the (uphill) -Z side.
const STAIR_SPAWN_EDGE_FRACTION = 0.4; // fraction of one tread forward of the top step's center, toward its downhill (+Z) edge
// A steep tilt + a big shove launches the rig clear over the whole staircase
// (measured: pelvis went from the spawn height to ground level in ~300ms,
// clearing all 5 risers without ever contacting one) instead of tumbling down
// it riser by riser. Tuned down to a modest lean + nudge so gravity and
// contact with each step do the cascading, not a ballistic arc over them.
const STAIR_SPAWN_CLEARANCE_M = 0.12; // vertical gap above the top step before the drop
const STAIR_SPAWN_TILT_RAD = Math.PI * 0.14; // forward pitch leaning toward the downhill (+Z) direction
const STAIR_SPAWN_FORWARD_VEL_MPS = 0.7; // initial downhill (+Z) nudge so the rig tips over the edge rather than pivoting in place
const STAIR_SPAWN_DROP_VEL_MPS = -0.15;
const STAIR_SPAWN_SPIN_RAD_S = 2.5; // forward tumble spin about X, same sense as the downhill lean, to keep the rig rolling across each riser

const HIT_LEAD_IN_S = 0.5;
const HIT_BONES: readonly HumanoidBoneName[] = ['leftUpperArm', 'rightUpperArm', 'head', 'spine'];
const HIT_IMPULSE_MIN_NS = 2.5;
const HIT_IMPULSE_MAX_NS = 6;

const DRAG_LEAD_IN_S = 1;
const DRAG_DURATION_S = 3;
const DRAG_DISTANCE_M = 2.5;
const DRAG_BONE: HumanoidBoneName = 'rightLowerArm';
const HAND_MAX_SPEED_MPS = 6;

const BALANCE_NUDGE_NS = 30;
const KNOCKDOWN_TILT_DEG = 55;

// muscleBalance user pushes — deterministic; only from explicit user/eval APIs.
// Checkpoint 22 two-tier contract (cp21 evidence):
//   certified MVP resistance: lateral ±2 N·s, sagittal ±4 N·s
//   stress / visible limit: lateral 80 N·s, sagittal 70 N·s (expected FALLEN)
export type MusclePushTier = 'certified' | 'stress';

/** Certified MVP lateral impulse (N·s) — normal push buttons / eval gate. */
export const MUSCLE_PUSH_CERTIFIED_LATERAL_NS = 2;
/** Certified MVP sagittal impulse (N·s). */
export const MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS = 4;
/** Stress-limit lateral impulse (N·s) — expected visible fall in the interactive route. */
export const MUSCLE_PUSH_STRESS_LATERAL_NS = 80;
/** Stress-limit sagittal impulse (N·s) — expected visible fall in the interactive route. */
export const MUSCLE_PUSH_STRESS_SAGITTAL_NS = 70;

/** @deprecated use MUSCLE_PUSH_CERTIFIED_LATERAL_NS; alias for certified default. */
export const MUSCLE_PUSH_LATERAL_IMPULSE_NS = MUSCLE_PUSH_CERTIFIED_LATERAL_NS;
/** @deprecated use MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS; alias for certified default. */
export const MUSCLE_PUSH_SAGITTAL_IMPULSE_NS = MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS;

const MUSCLE_PUSH_POINT_Y_OFFSET_M = 0.35;

export function musclePushImpulseMagnitudeNs(
  direction: MuscleBalancePushDirection,
  tier: MusclePushTier = 'certified',
): number {
  const lateral = tier === 'stress' ? MUSCLE_PUSH_STRESS_LATERAL_NS : MUSCLE_PUSH_CERTIFIED_LATERAL_NS;
  const sagittal = tier === 'stress' ? MUSCLE_PUSH_STRESS_SAGITTAL_NS : MUSCLE_PUSH_CERTIFIED_SAGITTAL_NS;
  return direction === 'left' || direction === 'right' ? lateral : sagittal;
}

export function musclePushImpulseVectorNs(
  direction: MuscleBalancePushDirection,
  tier: MusclePushTier = 'certified',
): readonly [number, number, number] {
  const m = musclePushImpulseMagnitudeNs(direction, tier);
  switch (direction) {
    case 'left':
      return [-m, 0, 0];
    case 'right':
      return [m, 0, 0];
    case 'forward':
      return [0, 0, m];
    case 'backward':
      return [0, 0, -m];
  }
}
const MUSCLE_DEFAULT_SEED = 0;
/** Demo fatigue leaves roughly 20% of normal torque capacity. */
const MUSCLE_FATIGUE_MODE_LEVEL = 0.85;
/** Elevated fatigue gain while fatigue mode is on. */
const MUSCLE_FATIGUE_MODE_GAIN_PER_S = 0.55;
/** Demo weakness is deliberately obvious while still leaving bounded torque. */
const MUSCLE_WEAK_DEMO_CAPACITY = 0.08;
const MUSCLE_FATIGUE_DEMO_TORQUE_SCALE = 0.75;
const MUSCLE_PUSH_RESPONSE_SUPPORT_M = 0.03;
const MUSCLE_PUSH_RESPONSE_TILT_DEG = 5;
const MUSCLE_PUSH_RESPONSE_SPEED_MPS = 0.05;
const MUSCLE_PUSH_RECOVERY_SUPPORT_M = 0.08;
const MUSCLE_PUSH_RECOVERY_TILT_DEG = 15;
const MUSCLE_PUSH_RECOVERY_SPEED_MPS = 0.12;
const MUSCLE_PUSH_RECOVERY_DWELL_S = 0.25;

// Tap/click-to-shove (Alive mode): a pointer tap on a body part applies an
// impulse in the camera-forward direction, magnitude scaled a bit by how far
// the hit point is from the rig's own center (more leverage at the
// extremities, same spirit as fireHitImpulse's random range above).
//
// 25-40N*s, not the plan's illustrative "8-15N*s": measured via a throwaway
// headed Playwright probe (repeated on-canvas taps at chest height, ~60ms
// apart) that 8-15N*s per tap, even 40 rapid taps in a row, never crossed
// KNOCKDOWN_TILT_DEG at all -- the live marionette COM-correction (running
// continuously between taps, same as the `fireSmashImpulse` finding below)
// absorbed each one before the next landed. 25-40N*s reliably tips him over
// within ~15-20 rapid taps, which is what "harder repeated taps knock him
// down" actually requires in practice.
const SHOVE_IMPULSE_MIN_NS = 25;
const SHOVE_IMPULSE_MAX_NS = 40;
const SHOVE_DISTANCE_SCALE_MIN = 0.7;
const SHOVE_DISTANCE_SCALE_MAX = 1.5;

// Auto get-up (Alive/Hit-post-impact): once knocked down, wait for the rig
// to actually settle (not mid-tumble) before engaging the get-up controller,
// matching the "settles for ~1.5s" behavior from the plan.
const KNOCKDOWN_GETUP_DELAY_S = 1.5;
// Safety valve: `applyGetUp` (humanoidMotor.ts) can get stuck hoisted-but-
// mis-oriented after a sideways-shove knockdown and never itself report
// tapered (see that function's block comment for the measured limitation).
// Cap how long this file waits before handing off to plain stand-assist
// regardless -- the knockdown/settle check above will simply re-trigger
// another get-up attempt if it's still down, so this keeps the shove ->
// crumple -> get-up loop self-correcting instead of stuck forever in the
// boosted-assist state.
const MAX_GET_UP_DURATION_S = 8;

const SLEEP_SWEEP_INTERVAL_S = 2;
const SLEEP_MOVE_EPS_M = 0.01;

export interface HumanoidLabCaps {
  awakeBodyCount: boolean;
  bodyCount: boolean;
  /** Solver-integrated joint motors (v0.5, see humanoidMotor.ts's file
   *  header) -- probed once at runtime creation, threaded through every
   *  `updateHumanoidActiveControl` call so a stale wasm build transparently
   *  falls back to the pre-v0.5 impulse-PD control law. */
  jointMotors: boolean;
  /** Solver motor-torque readback after step (Gate A saturation cert). */
  jointMotorTelemetry: boolean;
  /** Contact-end drain backed by native export (Phase 0 / Gate 0). */
  contactEndEvents: boolean;
  /** Contact-hit drain backed by native export (Phase 0 / Gate 0). */
  contactHitEvents: boolean;
  /** Per-body post-solver normal impulse telemetry (P1A load transfer). */
  contactLoadTelemetry: boolean;
}

/** Static scenario geometry (stair boxes) described in render-friendly terms
 *  — position + half-extents — so humanoidVisual.ts can build meshes for
 *  bodies that never move without needing a per-frame transform sync. */
export interface StaticBoxGeometry {
  readonly position: readonly [number, number, number];
  readonly halfExtentsM: readonly [number, number, number];
}

export interface HumanoidLabRuntime {
  world: World;
  caps: HumanoidLabCaps;
  stepper: FixedStepper;
  buffer: TransformBuffer;
  sleep: SleepManager;

  scenario: HumanoidScenario;
  scenarioVersion: number;
  scenarioElapsedS: number;
  hitFired: boolean;
  dragStarted: boolean;

  limitPreset: JointLimitPreset;
  heightM: number;
  massKg: number;
  poseHoldStrength: number;
  poseHoldRamp: PoseHoldRampState;
  poseHoldActive: boolean;
  /** Whether the boosted get-up controller (humanoidMotor.ts's `applyGetUp`)
   *  is currently hoisting the rig back to a stand. Additive field — E2E
   *  bridge shape otherwise unchanged. */
  gettingUp: boolean;
  getUpState: GetUpState;
  knockedDownSettledS: number;
  getUpElapsedS: number;

  humanoids: Humanoid[];
  trackedBodies: BodyHandle[];
  stairBodies: BodyHandle[];
  stairGeometry: StaticBoxGeometry[];
  /** Exact flat-ground BodyHandle created at runtime construction (never torn down with scenarios). */
  groundBody: BodyHandle;

  handBodyId: BodyHandle | 0;
  dragJoint: JointHandle | 0;
  dragging: boolean;
  handPos: { x: number; y: number; z: number };
  dragTarget: { x: number; y: number; z: number };
  spawnPelvisXZ: { x: number; z: number };

  settleGood: number;
  settled: boolean;

  fixedTime: number;
  stepMs: number;
  awakeCount: number;
  bodyCountTotal: number;
  pelvisHeight: number;
  pelvisDisplacementM: number;

  bodyIds: Int32Array;
  transforms: Float32Array;
  prevTransforms: Float32Array;
  prevValid: boolean;

  staticBodyCount: number;

  // --- muscleBalance (additive; unused by other scenarios) ---
  /** Pure controller state; reset deterministically on spawn/reset. */
  muscleState: MuscleControllerState;
  muscleSeed: number;
  muscleWeakLeftLeg: boolean;
  /** When true, injects elevated fatigue and blocks recovery (demo toggle). */
  muscleFatigueMode: boolean;
  /** Last user push direction (telemetry + observation); null until a push. */
  muscleLastPushDirection: MusclePushDirection;
  /** Last push tier (certified MVP vs stress limit). */
  muscleLastPushTier: MusclePushTier | null;
  /** Last push magnitude (N·s). */
  muscleLastPushImpulseNs: number;
  musclePushLifecycle: MusclePushLifecycle;
  musclePushElapsedS: number;
  musclePushRecoveredDwellS: number;
  musclePushDisturbed: boolean;
  musclePushPeakSupportErrorM: number;
  musclePushPeakTiltDeg: number;
  musclePushPeakDisplacementM: number;
  /** UI/API stress mode: primary fire uses the documented fall boundary when true. */
  musclePushStressMode: boolean;
  /** Latest muscle controller output snapshot for HUD / E2E. */
  muscleTelemetry: HumanoidMuscleTelemetry;
  /**
   * Measurement-only override: when true, forces `stepEnabled` for forced
   * capture-step baseline trials. Default false — does not change shipped demo.
   */
  muscleStepEnabledForMeasurement: boolean;
  /**
   * Evaluation/search-only P1A sagittal policy offsets. Null = baseline (zero).
   * Never set by UI; does not change production defaults.
   */
  muscleP1aSagittalPolicyOffsets: Readonly<P1ASagittalPolicyOffsets> | null;
  /** Gate 0 foot/ground contact lifecycle (begin/end/hit). */
  footContactTracker: FootContactTracker;
  /** Post-solver load samples for the latest fixed step; reused without allocation. */
  footContactLoads: HumanoidFootContactLoads;
  /** Bounded locomotion measurement buffer (additive; not used by UI). */
  locomotionTrace: LocomotionTraceBuffer;
  /**
   * Per-fixed-step commanded motor maxTorque + speed/velocity magnitude for
   * active humanoid joints (filled by muscle control before world.step).
   */
  motorCommands: HumanoidMotorCommandBuffer;
  /**
   * Post-step solver motor torque telemetry snapshot (Gate A saturation).
   * Empty when `jointMotorTelemetry` is false or no motors were commanded.
   */
  motorTelemetry: HumanoidMotorTelemetrySnapshot;
}

let modulePromise: Promise<Box3D> | null = null;
let runtime: HumanoidLabRuntime | null = null;
let runtimePromise: Promise<HumanoidLabRuntime> | null = null;
let runtimeFailed = false;

function loadModule(): Promise<Box3D> {
  if (!modulePromise) modulePromise = createBox3D();
  return modulePromise;
}

export function getExistingHumanoidLabRuntime(): HumanoidLabRuntime | null {
  return runtime;
}

export async function getHumanoidLabRuntime(
  initialScenario: HumanoidScenario = 'drop',
): Promise<HumanoidLabRuntime> {
  if (runtime) return runtime;
  if (runtimeFailed) throw new Error('Humanoid lab physics failed to initialize');

  if (!runtimePromise) {
    runtimePromise = createHumanoidLabRuntime(initialScenario)
      .then((created) => {
        runtime = created;
        return created;
      })
      .catch((error: unknown) => {
        runtimeFailed = true;
        runtimePromise = null;
        throw error;
      });
  }

  return runtimePromise;
}

function emptyGroupActivation(): Record<MuscleGroupName, number> {
  const out = {} as Record<MuscleGroupName, number>;
  for (const name of MUSCLE_GROUP_NAMES) out[name] = 0;
  return out;
}

function emptyMuscleTelemetry(state?: MuscleControllerState): HumanoidMuscleTelemetry {
  return {
    controllerActive: false,
    controlMode: 'muscle-inspired',
    phase: 'idle',
    peakActivation: 0,
    meanActivation: 0,
    fatigue: 0,
    weakLeftLeg: false,
    centerOfMass: [0, 0, 0],
    supportCenter: [0, 0, 0],
    supportErrorM: 0,
    pelvisTiltDeg: 0,
    lastPushDirection: null,
    lastPushTier: null,
    lastPushImpulseNs: 0,
    pushLifecycle: 'idle',
    pushElapsedS: 0,
    pushPeakSupportErrorM: 0,
    pushPeakTiltDeg: 0,
    pushPeakDisplacementM: 0,
    pushStressMode: false,
    forbiddenAssistCount: 0,
    groupActivation: emptyGroupActivation(),
    stateHash: hashMuscleControllerState(state ?? createMuscleControllerState(MUSCLE_DEFAULT_SEED)),
    stepPhase: 'none',
    swingSide: null,
    stepCount: 0,
    stepTargetXM: 0,
    stepTargetZM: 0,
    stepTargetWorldXM: 0,
    stepTargetWorldYM: 0,
    stepTargetWorldZM: 0,
    stepLandingErrorM: 0,
    supportSpanBeforeM: 0,
    supportSpanAfterM: 0,
    prepareDurationS: 0,
    comToStanceM: 0,
    minComToStanceM: 0,
    comLateralToStanceM: 0,
    comSagittalToStanceM: 0,
    minComLateralToStanceM: 0,
    minComSagittalToStanceM: 0,
    swingLiftM: 0,
    prepareAborted: false,
    prepareStancePlantErrorM: 0,
    minPrepareStancePlantErrorM: 0,
    maxPrepareStancePlantErrorM: 0,
    prepareReadyDwellS: 0,
    maxPrepareReadyDwellS: 0,
    leftFootContactPointCount: 0,
    rightFootContactPointCount: 0,
    leftFootNormalImpulse: 0,
    rightFootNormalImpulse: 0,
    combinedFootNormalImpulse: 0,
    leftFootLoadFraction: 0,
    rightFootLoadFraction: 0,
    stepArmed: true,
    plantedPoseActive: false,
    recoveredDwellS: 0,
    integralErrorXM: 0,
    integralErrorZM: 0,
    integralCorrectionXM: 0,
    integralCorrectionZM: 0,
  };
}

function telemetryFromMuscleOutput(
  out: MuscleControllerOutput,
  state: MuscleControllerState,
  controllerActive: boolean,
): HumanoidMuscleTelemetry {
  return {
    controllerActive,
    controlMode: out.controlMode,
    phase: out.phase,
    peakActivation: out.peakActivation,
    meanActivation: out.meanActivation,
    fatigue: out.fatigue,
    weakLeftLeg: out.weakLeftLeg,
    centerOfMass: out.centerOfMass,
    supportCenter: out.supportCenter,
    supportErrorM: out.supportErrorM,
    pelvisTiltDeg: out.pelvisTiltDeg,
    lastPushDirection: out.lastPushDirection,
    // Tier/magnitude are runtime-owned (user push APIs); preserved by caller merge.
    lastPushTier: null,
    lastPushImpulseNs: 0,
    pushLifecycle: 'idle',
    pushElapsedS: 0,
    pushPeakSupportErrorM: 0,
    pushPeakTiltDeg: 0,
    pushPeakDisplacementM: 0,
    pushStressMode: false,
    forbiddenAssistCount: out.forbiddenAssistCount,
    groupActivation: { ...out.groupActivation },
    stateHash: hashMuscleControllerState(state),
    stepPhase: out.stepPhase,
    swingSide: out.swingSide,
    stepCount: out.stepCount,
    stepTargetXM: out.stepTargetXM,
    stepTargetZM: out.stepTargetZM,
    stepTargetWorldXM: out.stepTargetWorldXM,
    stepTargetWorldYM: out.stepTargetWorldYM,
    stepTargetWorldZM: out.stepTargetWorldZM,
    stepLandingErrorM: out.stepLandingErrorM,
    supportSpanBeforeM: out.supportSpanBeforeM,
    supportSpanAfterM: out.supportSpanAfterM,
    prepareDurationS: out.prepareDurationS,
    comToStanceM: out.comToStanceM,
    minComToStanceM: out.minComToStanceM,
    comLateralToStanceM: out.comLateralToStanceM,
    comSagittalToStanceM: out.comSagittalToStanceM,
    minComLateralToStanceM: out.minComLateralToStanceM,
    minComSagittalToStanceM: out.minComSagittalToStanceM,
    swingLiftM: out.swingLiftM,
    prepareAborted: out.prepareAborted,
    prepareStancePlantErrorM: out.prepareStancePlantErrorM,
    minPrepareStancePlantErrorM: out.minPrepareStancePlantErrorM,
    maxPrepareStancePlantErrorM: out.maxPrepareStancePlantErrorM,
    prepareReadyDwellS: out.prepareReadyDwellS,
    maxPrepareReadyDwellS: out.maxPrepareReadyDwellS,
    leftFootContactPointCount: 0,
    rightFootContactPointCount: 0,
    leftFootNormalImpulse: 0,
    rightFootNormalImpulse: 0,
    combinedFootNormalImpulse: 0,
    leftFootLoadFraction: 0,
    rightFootLoadFraction: 0,
    stepArmed: out.stepArmed,
    plantedPoseActive: out.plantedPoseActive,
    recoveredDwellS: out.recoveredDwellS,
    integralErrorXM: out.integralErrorXM,
    integralErrorZM: out.integralErrorZM,
    integralCorrectionXM: out.integralCorrectionXM,
    integralCorrectionZM: out.integralCorrectionZM,
  };
}

function resetMuscleRuntimeState(rt: HumanoidLabRuntime, seed = rt.muscleSeed): void {
  rt.muscleSeed = seed | 0;
  resetMuscleController(rt.muscleState, rt.muscleSeed);
  // Evaluator-only capture stepping must never survive a controller/scenario reset.
  rt.muscleStepEnabledForMeasurement = false;
  // Evaluation-only P1A policy offsets must not leak across trials/resets.
  rt.muscleP1aSagittalPolicyOffsets = null;
  rt.muscleLastPushDirection = null;
  rt.muscleLastPushTier = null;
  rt.muscleLastPushImpulseNs = 0;
  rt.musclePushLifecycle = 'idle';
  rt.musclePushElapsedS = 0;
  rt.musclePushRecoveredDwellS = 0;
  rt.musclePushDisturbed = false;
  rt.musclePushPeakSupportErrorM = 0;
  rt.musclePushPeakTiltDeg = 0;
  rt.musclePushPeakDisplacementM = 0;
  // Stress mode is a UI preference — keep across soft controller reset so the
  // toggle does not silently clear; full scenario respawn recreates runtime.
  rt.muscleWeakLeftLeg = false;
  rt.muscleFatigueMode = false;
  rt.muscleTelemetry = {
    ...emptyMuscleTelemetry(rt.muscleState),
    pushStressMode: rt.musclePushStressMode,
    pushLifecycle: 'idle',
    pushElapsedS: 0,
    pushPeakSupportErrorM: 0,
    pushPeakTiltDeg: 0,
    pushPeakDisplacementM: 0,
  };
  resetFootContactLoads(rt);
}

/**
 * Isolated runtime factory for unit tests. Does not install the page singleton
 * (`getHumanoidLabRuntime`). Prefer this in Vitest so tests do not share state.
 */
export async function createHumanoidLabRuntime(initialScenario: HumanoidScenario = 'drop'): Promise<HumanoidLabRuntime> {
  const box3d = await loadModule();
  const world = box3d.createWorld({ gravity: [0, -9.81, 0] });
  const probed = probeCapabilities(world);
  const caps: HumanoidLabCaps = {
    awakeBodyCount: probed.awakeBodyCount,
    bodyCount: probed.bodyCount,
    jointMotors: probed.jointMotors,
    jointMotorTelemetry: probed.jointMotorTelemetry,
    contactEndEvents: probed.contactEndEvents,
    contactHitEvents: probed.contactHitEvents,
    contactLoadTelemetry: probed.contactLoadTelemetry,
  };

  const muscleState = createMuscleControllerState(MUSCLE_DEFAULT_SEED);
  // Placeholder ground handle filled by addGround before any spawn.
  const rt: HumanoidLabRuntime = {
    world,
    caps,
    stepper: new FixedStepper({ fixedDt: FIXED_DT, substeps: SUBSTEPS, maxStepsPerFrame: MAX_STEPS_PER_FRAME }),
    buffer: new TransformBuffer(8),
    sleep: new SleepManager(world, { sweepIntervalSec: SLEEP_SWEEP_INTERVAL_S, moveThreshold: SLEEP_MOVE_EPS_M }),

    scenario: initialScenario,
    scenarioVersion: 0,
    scenarioElapsedS: 0,
    hitFired: false,
    dragStarted: false,

    limitPreset: 'game',
    heightM: DEFAULT_HEIGHT_M,
    massKg: DEFAULT_MASS_KG,
    poseHoldStrength: 1,
    poseHoldRamp: createPoseHoldRampState(0),
    poseHoldActive: false,
    gettingUp: false,
    getUpState: createGetUpState(),
    knockedDownSettledS: 0,
    getUpElapsedS: 0,

    humanoids: [],
    trackedBodies: [],
    stairBodies: [],
    stairGeometry: [],
    groundBody: 0 as BodyHandle,

    handBodyId: 0,
    dragJoint: 0,
    dragging: false,
    handPos: { x: 0, y: 0, z: 0 },
    dragTarget: { x: 0, y: 0, z: 0 },
    spawnPelvisXZ: { x: 0, z: 0 },

    settleGood: 0,
    settled: false,

    fixedTime: 0,
    stepMs: 0,
    awakeCount: 0,
    bodyCountTotal: 0,
    pelvisHeight: 0,
    pelvisDisplacementM: 0,

    bodyIds: new Int32Array(0),
    transforms: new Float32Array(0),
    prevTransforms: new Float32Array(0),
    prevValid: false,

    staticBodyCount: 0,

    muscleState,
    muscleSeed: MUSCLE_DEFAULT_SEED,
    muscleWeakLeftLeg: false,
    muscleFatigueMode: false,
    muscleLastPushDirection: null,
    muscleLastPushTier: null,
    muscleLastPushImpulseNs: 0,
    musclePushLifecycle: 'idle',
    musclePushElapsedS: 0,
    musclePushRecoveredDwellS: 0,
    musclePushDisturbed: false,
    musclePushPeakSupportErrorM: 0,
    musclePushPeakTiltDeg: 0,
    musclePushPeakDisplacementM: 0,
    musclePushStressMode: false,
    muscleTelemetry: emptyMuscleTelemetry(muscleState),
    muscleStepEnabledForMeasurement: false,
    muscleP1aSagittalPolicyOffsets: null,
    footContactTracker: createFootContactTracker(),
    footContactLoads: {
      left: { pointCount: 0, totalNormalImpulse: 0 },
      right: { pointCount: 0, totalNormalImpulse: 0 },
    },
    locomotionTrace: createLocomotionTraceBuffer(LOCOMOTION_TRACE_MAX_SAMPLES, FIXED_DT),
    motorCommands: createMotorCommandBuffer(),
    motorTelemetry: emptyMotorTelemetrySnapshot(),
  };

  addGround(rt);
  spawnScenario(rt, initialScenario);
  return rt;
}

function addGround(rt: HumanoidLabRuntime): void {
  const ground = rt.world.createBody({ type: 'static', position: [0, -0.5, 0] });
  rt.world.addBox(ground, [40, 0.5, 40], { friction: 0.85, restitution: 0.02 });
  rt.groundBody = ground;
  rt.staticBodyCount += 1;
}

/**
 * Discard begin/end/hit/sensor queues without attributing them to a live humanoid.
 * Call immediately before body teardown / contact-generation advance.
 */
function discardContactEventQueues(rt: HumanoidLabRuntime): void {
  rt.world.drainContactBeginEvents();
  rt.world.drainContactEndEvents();
  rt.world.drainContactHitEvents();
  rt.world.drainSensorEvents();
}

/**
 * Drain contact events into the foot tracker once per fixed step.
 * Order: end → begin → hit (tracker reconciles; soft contacts need no hit).
 */
function ingestFootContactsAfterStep(rt: HumanoidLabRuntime): void {
  const ends = rt.world.drainContactEndEvents();
  const begins = rt.world.drainContactBeginEvents();
  // Hits are optional evidence; always drain so the queue cannot leak into
  // another scenario or body generation.
  const hits = rt.world.drainContactHitEvents();
  rt.footContactTracker.ingestStep({ ends, begins, hits });
}

/** Bind tracker to the current ground + primary humanoid feet only. */
function bindFootContactTracker(rt: HumanoidLabRuntime): void {
  const humanoid = rt.humanoids[0];
  if (!humanoid || !rt.groundBody) {
    // Leave unbound; stale handles must not be attributed.
    return;
  }
  rt.footContactTracker.bind({
    ground: rt.groundBody,
    leftFoot: humanoid.bodies.leftFoot,
    rightFoot: humanoid.bodies.rightFoot,
  });
}

// --- scenario spawn/teardown ---

function baseSpec(rt: HumanoidLabRuntime, overrides: HumanoidSpecInput): HumanoidSpecInput {
  return {
    height: rt.heightM,
    mass: rt.massKg,
    limitPreset: rt.limitPreset,
    ...overrides,
  };
}

function quatAroundX(angleRad: number): readonly [number, number, number, number] {
  return [Math.sin(angleRad / 2), 0, 0, Math.cos(angleRad / 2)];
}

function buildStairs(rt: HumanoidLabRuntime): void {
  const halfExtents: readonly [number, number, number] = [STAIR_WIDTH_M / 2, STAIR_RISE_M / 2, STAIR_RUN_M / 2];
  for (let i = 0; i < STAIR_COUNT; i += 1) {
    const topY = (i + 1) * STAIR_RISE_M;
    const centerY = topY - STAIR_RISE_M / 2;
    const centerZ = -i * STAIR_RUN_M; // tallest step at the most-negative Z; staircase descends toward +Z
    const position: readonly [number, number, number] = [0, centerY, centerZ];
    const body = rt.world.createBody({ type: 'static', position });
    rt.world.addBox(body, halfExtents, { friction: 0.7, restitution: 0.05 });
    rt.stairBodies.push(body);
    rt.stairGeometry.push({ position, halfExtentsM: halfExtents });
  }
}

function spawnDrop(rt: HumanoidLabRuntime): void {
  rt.humanoids = [createHumanoid(rt.world, baseSpec(rt, { position: [0, DROP_HEIGHT_M, 0] }))];
}

function spawnStairs(rt: HumanoidLabRuntime): void {
  buildStairs(rt);
  const topY = STAIR_COUNT * STAIR_RISE_M;
  const topStepCenterZ = -(STAIR_COUNT - 1) * STAIR_RUN_M;
  const spawnZ = topStepCenterZ + STAIR_RUN_M * STAIR_SPAWN_EDGE_FRACTION;
  const humanoid = createHumanoid(
    rt.world,
    baseSpec(rt, {
      position: [0, topY + STAIR_SPAWN_CLEARANCE_M, spawnZ],
      // Spawn already tipped forward over the top step's downhill (+Z) edge:
      // a passive ragdoll balanced upright at a stair edge would just stand
      // there, not "tumble down 5 steps" as the spec's scenario calls for.
      rotation: quatAroundX(STAIR_SPAWN_TILT_RAD),
    }),
  );
  rt.humanoids = [humanoid];
  // A rig merely tipped over the edge pitches forward and arrests on the very
  // next step (measured) rather than cascading the full staircase. Give every
  // body the same initial downhill shove + forward tumble spin so momentum
  // actually carries it across all 5 risers.
  for (const bone of HUMANOID_BONE_NAMES) {
    const body = humanoid.bodies[bone];
    rt.world.setLinearVelocity(body, [0, STAIR_SPAWN_DROP_VEL_MPS, STAIR_SPAWN_FORWARD_VEL_MPS]);
    rt.world.setAngularVelocity(body, [STAIR_SPAWN_SPIN_RAD_S, 0, 0]);
  }
}

function spawnHit(rt: HumanoidLabRuntime): void {
  rt.humanoids = [createHumanoid(rt.world, baseSpec(rt, { position: [0, 0.03, 0] }))];
}

function spawnDrag(rt: HumanoidLabRuntime): void {
  rt.humanoids = [createHumanoid(rt.world, baseSpec(rt, { position: [0, 0.05, 0] }))];
}

function spawnPile(rt: HumanoidLabRuntime): void {
  rt.humanoids = createHumanoidCrowd(rt.world, {
    count: PILE_COUNT,
    spec: baseSpec(rt, { position: [0, PILE_DROP_HEIGHT_M, 0] }),
    layout: 'grid',
    spacing: PILE_SPACING_M,
    seedBase: 300,
  });
}

function spawnBalance(rt: HumanoidLabRuntime): void {
  rt.humanoids = [createHumanoid(rt.world, baseSpec(rt, { position: [0, 0.02, 0] }))];
}

function spawnMuscleBalance(rt: HumanoidLabRuntime): void {
  // Same grounded spawn as certified `balance`, but control path is separate.
  // Checkpoint 7 physical sweep: muscleBalance-only linearDamping 0.3 (default
  // rig 0.1) is required with ankle sign=+1 / gain=12 / motor maxSpeed=8 to
  // pass idle 10s. Scoped here so certified `balance` still uses default 0.1.
  // Checkpoint 12: anatomical feet (~10×26 cm sagittal, heel-biased ankle,
  // same volume/mass) are muscleBalance-only — balance/other scenarios keep
  // the legacy lateral-long default foot.
  const humanoid = createHumanoid(
    rt.world,
    baseSpec(rt, {
      position: [0, 0.02, 0],
      linearDamping: 0.3,
      footLayout: 'anatomical',
      // Checkpoint 20: spherical ankles (pitch+roll) — muscleBalance only.
      jointLayout: 'muscleBalance',
    }),
  );
  rt.humanoids = [humanoid];
}

function teardownScenario(rt: HumanoidLabRuntime): void {
  endHumanoidDrag(rt);
  const hadScenarioBodies = rt.humanoids.length > 0 || rt.stairBodies.length > 0;
  // Motors persist on joints until destroyed; clear before body teardown when
  // leaving a motor-driven scenario so nothing leaks if destroy order changes.
  if (rt.scenario === 'muscleBalance' || rt.scenario === 'balance' || rt.scenario === 'hit') {
    for (const humanoid of rt.humanoids) disableAllJointMotors(rt.world, humanoid);
  }
  // Gate 0: drain/discard all event queues and advance contact generation
  // BEFORE destroying bodies so stale end events (handle 0 / reused ids)
  // cannot be attributed to the next Manny.
  discardContactEventQueues(rt);
  rt.footContactTracker.resetAndAdvanceGeneration();
  rt.locomotionTrace.reset(FIXED_DT);
  resetFootContactLoads(rt);

  for (const humanoid of rt.humanoids) humanoid.destroy(rt.world);
  rt.humanoids = [];
  for (const body of rt.stairBodies) rt.world.destroyBody(body);
  rt.stairBodies = [];
  rt.stairGeometry = [];
  if (hadScenarioBodies) {
    // Destroy emits native end-touch events after the pre-teardown queues were
    // drained. Step only the persistent static ground while the tracker is
    // unbound, then discard those orphaned (body 0) events before replacement
    // handles can be allocated and rebound.
    rt.world.step(FIXED_DT, SUBSTEPS);
    discardContactEventQueues(rt);
  }
  rt.prevValid = false;
}

function rebuildTransformBuffer(rt: HumanoidLabRuntime): void {
  const capacity = Math.max(rt.trackedBodies.length, 1);
  rt.buffer = new TransformBuffer(capacity);
  for (const body of rt.trackedBodies) rt.buffer.add(body);
  rt.buffer.rebuild();
  rt.bodyIds = rt.buffer.ids;
  rt.transforms = rt.buffer.transforms;
  rt.prevTransforms = new Float32Array(rt.trackedBodies.length * 7);
  rt.prevValid = false;
}

function spawnScenario(rt: HumanoidLabRuntime, scenario: HumanoidScenario): void {
  teardownScenario(rt);
  rt.scenario = scenario;
  rt.scenarioVersion += 1;
  rt.scenarioElapsedS = 0;
  rt.hitFired = false;
  rt.dragStarted = false;
  rt.settleGood = 0;
  rt.settled = false;
  rt.poseHoldActive = false;
  // Certified `balance` still ramps pose-hold from full strength; muscleBalance
  // does not use pose-hold / stand-assist at all.
  rt.poseHoldRamp = createPoseHoldRampState(scenario === 'balance' ? 1 : 0);
  rt.gettingUp = false;
  resetGetUpState(rt.getUpState);
  rt.knockedDownSettledS = 0;
  rt.getUpElapsedS = 0;
  // Always clear muscle toggles/state on any scenario spawn so a prior
  // muscleBalance demo cannot leak weakness/fatigue into the next run.
  resetMuscleRuntimeState(rt, rt.muscleSeed);

  if (scenario === 'drop') spawnDrop(rt);
  else if (scenario === 'stairs') spawnStairs(rt);
  else if (scenario === 'hit') spawnHit(rt);
  else if (scenario === 'drag') spawnDrag(rt);
  else if (scenario === 'pile') spawnPile(rt);
  else if (scenario === 'muscleBalance') spawnMuscleBalance(rt);
  else spawnBalance(rt);

  rt.trackedBodies = rt.humanoids.flatMap((humanoid) => HUMANOID_BONE_NAMES.map((bone) => humanoid.bodies[bone]));
  rebuildTransformBuffer(rt);
  rt.buffer.readInto(rt.world);
  rt.sleep.watch(rt.trackedBodies, rt.buffer);

  // Bind only current ground/foot handles after spawn (post-generation advance).
  bindFootContactTracker(rt);

  const primary = rt.humanoids[0];
  const hipsOffset = primary ? rt.buffer.offsetOf(primary.root) : undefined;
  rt.spawnPelvisXZ =
    hipsOffset !== undefined
      ? { x: rt.buffer.transforms[hipsOffset], z: rt.buffer.transforms[hipsOffset + 2] }
      : { x: 0, z: 0 };
}

/** Force a fresh spawn of the CURRENT scenario (the HUD's "Reset scenario" button). */
export function respawnHumanoidLabScenario(rt: HumanoidLabRuntime): void {
  spawnScenario(rt, rt.scenario);
}

export function setHumanoidLabScenario(rt: HumanoidLabRuntime, scenario: HumanoidScenario): void {
  if (rt.scenario === scenario) return;
  spawnScenario(rt, scenario);
}

export function setHumanoidLabHeight(rt: HumanoidLabRuntime, heightM: number): void {
  rt.heightM = heightM;
  spawnScenario(rt, rt.scenario);
}

export function setHumanoidLabMass(rt: HumanoidLabRuntime, massKg: number): void {
  rt.massKg = massKg;
  spawnScenario(rt, rt.scenario);
}

export function setHumanoidLabLimitPreset(rt: HumanoidLabRuntime, preset: JointLimitPreset): void {
  rt.limitPreset = preset;
  spawnScenario(rt, rt.scenario);
}

export function setHumanoidLabPoseHoldStrength(rt: HumanoidLabRuntime, value: number): void {
  rt.poseHoldStrength = Math.max(0, Math.min(1, value));
}

// --- one-shot user/scenario actions ---

export function fireHitImpulse(rt: HumanoidLabRuntime): void {
  const humanoid = rt.humanoids[0];
  if (!humanoid) return;
  rt.hitFired = true;
  const bone = HIT_BONES[Math.floor(Math.random() * HIT_BONES.length)];
  const body = humanoid.bodies[bone];
  const dirX = (Math.random() - 0.5) * 2;
  const dirY = Math.random() * 0.6 + 0.1;
  const dirZ = (Math.random() - 0.5) * 2;
  const len = Math.hypot(dirX, dirY, dirZ) || 1;
  const magnitude = HIT_IMPULSE_MIN_NS + Math.random() * (HIT_IMPULSE_MAX_NS - HIT_IMPULSE_MIN_NS);
  rt.world.applyImpulse(body, [(dirX / len) * magnitude, (dirY / len) * magnitude, (dirZ / len) * magnitude]);
}

export function fireBalanceNudge(rt: HumanoidLabRuntime): void {
  const humanoid = rt.humanoids[0];
  if (!humanoid || rt.scenario !== 'balance') return;
  const offset = rt.buffer.offsetOf(humanoid.root);
  if (offset === undefined) return;
  const t = rt.buffer.transforms;
  const point: readonly [number, number, number] = [t[offset], t[offset + 1] + 0.35, t[offset + 2]];
  const sign = Math.random() < 0.5 ? -1 : 1;
  rt.world.applyImpulse(humanoid.root, [BALANCE_NUDGE_NS * sign, 0, 0], point);
}

// --- muscleBalance user actions (impulses ONLY here; never in auto control) ---

export interface FireMusclePushOptions {
  /**
   * Impulse tier. Default: respect `rt.musclePushStressMode` when set, else
   * `certified` (2/4 N·s). Pass `stress` to force the fall boundary regardless of mode.
   */
  tier?: MusclePushTier;
}

/**
 * Deterministic directional push for muscleBalance only. User/evaluation
 * path — the automatic muscle controller never calls this.
 *
 * Default tier is **certified** MVP (lat 2 / sag 4 N·s). Stress tier
 * (lat 80 / sag 70) is for the explicit stress control / eval limit section.
 */
export function fireMuscleBalancePush(
  rt: HumanoidLabRuntime,
  direction: MuscleBalancePushDirection,
  opts?: FireMusclePushOptions,
): void {
  const humanoid = rt.humanoids[0];
  if (!humanoid || rt.scenario !== 'muscleBalance') return;
  const offset = rt.buffer.offsetOf(humanoid.root);
  if (offset === undefined) return;
  const tier: MusclePushTier =
    opts?.tier ?? (rt.musclePushStressMode ? 'stress' : 'certified');
  const impulse = musclePushImpulseVectorNs(direction, tier);
  const magnitude = musclePushImpulseMagnitudeNs(direction, tier);
  const t = rt.buffer.transforms;
  const point: readonly [number, number, number] = [
    t[offset],
    t[offset + 1] + MUSCLE_PUSH_POINT_Y_OFFSET_M,
    t[offset + 2],
  ];
  rt.world.applyImpulse(humanoid.root, impulse, point);
  rt.muscleLastPushDirection = direction;
  rt.muscleLastPushTier = tier;
  rt.muscleLastPushImpulseNs = magnitude;
  rt.musclePushLifecycle = 'pending';
  rt.musclePushElapsedS = 0;
  rt.musclePushRecoveredDwellS = 0;
  rt.musclePushDisturbed = false;
  rt.musclePushPeakSupportErrorM = rt.muscleTelemetry.supportErrorM;
  rt.musclePushPeakTiltDeg = rt.muscleTelemetry.pelvisTiltDeg;
  rt.musclePushPeakDisplacementM = 0;
  // Keep telemetry last-push fresh even between fixed steps.
  rt.muscleTelemetry = {
    ...rt.muscleTelemetry,
    lastPushDirection: direction,
    lastPushTier: tier,
    lastPushImpulseNs: magnitude,
    pushLifecycle: 'pending',
    pushElapsedS: 0,
    pushPeakSupportErrorM: rt.musclePushPeakSupportErrorM,
    pushPeakTiltDeg: rt.musclePushPeakTiltDeg,
    pushPeakDisplacementM: 0,
    pushStressMode: rt.musclePushStressMode,
  };
}

/** Force the stress-tier fall-boundary push regardless of UI mode. */
export function fireMuscleStressPush(
  rt: HumanoidLabRuntime,
  direction: MuscleBalancePushDirection,
): void {
  fireMuscleBalancePush(rt, direction, { tier: 'stress' });
}

export function setMusclePushStressMode(rt: HumanoidLabRuntime, enabled: boolean): void {
  rt.musclePushStressMode = !!enabled;
  if (rt.scenario === 'muscleBalance') {
    rt.muscleTelemetry = {
      ...rt.muscleTelemetry,
      pushStressMode: rt.musclePushStressMode,
    };
  }
}

/**
 * Measurement-only: force capture-step `stepEnabled` without changing the
 * shipped default (`false`). Used by Gate 0 forced-failure baselines.
 */
export function setMuscleStepEnabledForMeasurement(
  rt: HumanoidLabRuntime,
  enabled: boolean,
): void {
  rt.muscleStepEnabledForMeasurement = !!enabled;
}

/**
 * Evaluation/search-only: set P1A sagittal policy offsets on the runtime.
 * Pass `null` to clear (baseline zero). Does not change production defaults.
 * Applied only while the controller's single-support hold path is active.
 */
export function setHumanoidP1ASagittalPolicyOffsets(
  rt: HumanoidLabRuntime,
  offsets: Readonly<P1ASagittalPolicyOffsets> | null,
): void {
  rt.muscleP1aSagittalPolicyOffsets = offsets;
}

/**
 * Queue a single-use explicit forward step command (Gate A).
 * Does not enable automatic capture stepping (`stepEnabled` stays default).
 * Returns false if a step is already active or a pending request exists.
 */
export function requestHumanoidOneStep(
  rt: HumanoidLabRuntime,
  opts?: {
    forwardDisplacementM?: number;
    lateralDisplacementM?: number;
    swingSide?: MuscleSwingSide | null;
  },
): boolean {
  if (rt.scenario !== 'muscleBalance') return false;
  return queueMuscleOneStep(rt.muscleState, opts);
}

/**
 * Evaluation-only style knobs (imitation / P1A search). Not hashed into state
 * identity. Pass `null` to clear (production defaults).
 */
export function setHumanoidP1ATrialStyleKnobs(
  rt: HumanoidLabRuntime,
  knobs: Readonly<P1ATrialStyleKnobs> | null,
): void {
  if (rt.scenario !== 'muscleBalance') return;
  setP1ATrialStyleKnobs(rt.muscleState, knobs);
}

/** Read back evaluation-only style knobs (null when unset). */
export function getHumanoidP1ATrialStyleKnobs(
  rt: HumanoidLabRuntime,
): Readonly<P1ATrialStyleKnobs> | null {
  return rt.muscleState.p1aTrialStyleKnobs;
}

/**
 * Queue P1A single-support hold-and-replace diagnostic.
 * Does not enable automatic capture stepping. Mutually exclusive with Gate A
 * one-step. Returns false if already stepping or another request is pending.
 */
export function requestHumanoidSingleSupportHold(
  rt: HumanoidLabRuntime,
  opts?: {
    swingSide?: MuscleSwingSide | null;
  },
): boolean {
  if (rt.scenario !== 'muscleBalance') return false;
  return queueMuscleSingleSupportHold(rt.muscleState, opts);
}

/** True when locomotion certification may run (motors + torque telemetry + end/hit contact drains). */
export function humanoidLocomotionCapabilitiesReady(rt: HumanoidLabRuntime): boolean {
  return (
    rt.caps.jointMotors === true &&
    rt.caps.jointMotorTelemetry === true &&
    rt.caps.contactEndEvents === true &&
    rt.caps.contactHitEvents === true &&
    rt.caps.contactLoadTelemetry === true
  );
}

/**
 * Loud failure reason when required locomotion measurement capabilities are
 * missing. Returns null when ready. Does not fall back to height proxies.
 */
export function locomotionCapabilityFailureReason(rt: HumanoidLabRuntime): string | null {
  const missing: string[] = [];
  if (!rt.caps.jointMotors) missing.push('jointMotors');
  if (!rt.caps.jointMotorTelemetry) missing.push('jointMotorTelemetry');
  if (!rt.caps.contactEndEvents) missing.push('contactEndEvents');
  if (!rt.caps.contactHitEvents) missing.push('contactHitEvents');
  if (!rt.caps.contactLoadTelemetry) missing.push('contactLoadTelemetry');
  if (missing.length === 0) return null;
  return `locomotion certification requires capabilities: ${missing.join(', ')} (no height-proxy fallback)`;
}

/** Latest post-step joint motor telemetry snapshot (empty when unavailable). */
export function getHumanoidMotorTelemetry(rt: HumanoidLabRuntime): HumanoidMotorTelemetrySnapshot {
  return rt.motorTelemetry;
}

export function toggleMusclePushStressMode(rt: HumanoidLabRuntime): void {
  setMusclePushStressMode(rt, !rt.musclePushStressMode);
}

export function fireMusclePushLeft(rt: HumanoidLabRuntime, opts?: FireMusclePushOptions): void {
  fireMuscleBalancePush(rt, 'left', opts);
}

export function fireMusclePushRight(rt: HumanoidLabRuntime, opts?: FireMusclePushOptions): void {
  fireMuscleBalancePush(rt, 'right', opts);
}

export function fireMusclePushForward(rt: HumanoidLabRuntime, opts?: FireMusclePushOptions): void {
  fireMuscleBalancePush(rt, 'forward', opts);
}

export function fireMusclePushBackward(rt: HumanoidLabRuntime, opts?: FireMusclePushOptions): void {
  fireMuscleBalancePush(rt, 'backward', opts);
}

/** Toggle asymmetric left-leg weakness for the muscle controller. */
export function setHumanoidMuscleWeakLeftLeg(rt: HumanoidLabRuntime, enabled: boolean): void {
  rt.muscleWeakLeftLeg = !!enabled;
  if (rt.scenario === 'muscleBalance') {
    rt.muscleTelemetry = { ...rt.muscleTelemetry, weakLeftLeg: rt.muscleWeakLeftLeg };
  }
}

export function toggleHumanoidMuscleWeakLeftLeg(rt: HumanoidLabRuntime): void {
  setHumanoidMuscleWeakLeftLeg(rt, !rt.muscleWeakLeftLeg);
}

/**
 * Fatigue demo mode: when enabled, injects elevated fatigue and uses a high
 * gain / zero recovery so capacity stays reduced until toggled off or reset.
 */
export function setHumanoidMuscleFatigueMode(rt: HumanoidLabRuntime, enabled: boolean): void {
  rt.muscleFatigueMode = !!enabled;
  if (enabled) {
    rt.muscleState.fatigue = Math.max(rt.muscleState.fatigue, MUSCLE_FATIGUE_MODE_LEVEL);
  }
  if (rt.scenario === 'muscleBalance') {
    rt.muscleTelemetry = {
      ...rt.muscleTelemetry,
      fatigue: rt.muscleState.fatigue,
      stateHash: hashMuscleControllerState(rt.muscleState),
    };
  }
}

export function toggleHumanoidMuscleFatigueMode(rt: HumanoidLabRuntime): void {
  setHumanoidMuscleFatigueMode(rt, !rt.muscleFatigueMode);
}

/**
 * Deterministic muscle-controller reset without destroying the rig.
 * Clears activation/fatigue/push direction and re-seeds the controller.
 * Scenario geometry is unchanged (use `respawnHumanoidLabScenario` for a full re-spawn).
 */
export function resetHumanoidMuscleController(rt: HumanoidLabRuntime, seed = rt.muscleSeed): void {
  resetMuscleRuntimeState(rt, seed);
  // If motors were left targeted, clear them so limp state is honest until the
  // next muscle control step re-applies commands.
  if (rt.scenario === 'muscleBalance') {
    for (const humanoid of rt.humanoids) disableAllJointMotors(rt.world, humanoid);
  }
}

/** Read-only additive muscle telemetry snapshot (stable for E2E). */
export function getHumanoidMuscleTelemetry(rt: HumanoidLabRuntime): HumanoidMuscleTelemetry {
  return rt.muscleTelemetry;
}

// Near-pure sagittal (+Z, a touch of lift): a straight sagittal fall
// direction is what the get-up controller reliably recovers from (measured
// recoveredAtS ~0.7s in an isolated, no-prior-control fall via a throwaway
// diag script, since removed). A LATERAL/twisting shove was measured, by
// contrast, to leave the rig stuck hoisted-but-mis-oriented (see
// applyGetUp's block comment in humanoidMotor.ts for the full
// investigation) — this dev-only "smash" impulse deliberately avoids that
// failure mode.
//
// Magnitude is 550, not the plan's illustrative "60N*s": that number assumed
// hitting a passive statue. The rig here is ALREADY under continuous active
// stand-assist (unlike every diag script's cold-start fall) — the marionette
// COM-correction fights small-to-moderate perturbations in real time, and
// measured sweeps of 100/150/200/250N*s (throwaway diag script, since
// removed) were all absorbed without ever crossing KNOCKDOWN_TILT_DEG; 300
// and even 350 were close enough to that threshold to pass in isolation but
// fail intermittently once E2E ran them after other heavier scenarios in the
// same browser session (timing-sensitive, not a hard cliff) — picking a
// bigger margin above the measured threshold rather than a value that
// merely happened to work once.
const SMASH_IMPULSE: readonly [number, number, number] = [0, 3, 550];

/** Dev-only guaranteed-knockdown impulse for E2E (humanoid-lab.spec.ts's
 *  `data-testid="humanoid-smash"` button) — see SMASH_IMPULSE's comment for
 *  why this specific direction, not a random/lateral one. */
export function fireSmashImpulse(rt: HumanoidLabRuntime): void {
  const humanoid = rt.humanoids[0];
  if (!humanoid || rt.scenario !== 'balance') return;
  const offset = rt.buffer.offsetOf(humanoid.root);
  if (offset === undefined) return;
  const t = rt.buffer.transforms;
  const point: readonly [number, number, number] = [t[offset], t[offset + 1] + 0.35, t[offset + 2]];
  rt.world.applyImpulse(humanoid.root, SMASH_IMPULSE, point);
}

/**
 * Tap/click-to-shove (Alive mode): applies an impulse to `bone` along
 * `direction` (a normalized-or-not world-space vector, e.g. the camera's
 * forward direction — the page owns the raycast that picks `bone`).
 * `distanceScale` lets the caller give harder-hitting taps at the
 * extremities a bit more punch (clamped to a modest range so a single tap
 * never single-handedly exceeds fireHitImpulse's own magnitude range by
 * much). Balance-mode only, matching fireBalanceNudge.
 */
export function fireShoveImpulse(
  rt: HumanoidLabRuntime,
  bone: HumanoidBoneName,
  direction: readonly [number, number, number],
  distanceScale = 1,
): void {
  const humanoid = rt.humanoids[0];
  if (!humanoid || rt.scenario !== 'balance') return;
  const body = humanoid.bodies[bone];
  const len = Math.hypot(direction[0], direction[1], direction[2]) || 1;
  const magnitude = SHOVE_IMPULSE_MIN_NS + Math.random() * (SHOVE_IMPULSE_MAX_NS - SHOVE_IMPULSE_MIN_NS);
  const clampedScale = Math.max(SHOVE_DISTANCE_SCALE_MIN, Math.min(SHOVE_DISTANCE_SCALE_MAX, distanceScale));
  const scaled = magnitude * clampedScale;
  rt.world.applyImpulse(body, [(direction[0] / len) * scaled, (direction[1] / len) * scaled, (direction[2] / len) * scaled]);
}

// --- kinematic drag hand (yard's beginDrag/updateDragTarget/endDrag pattern) ---

function beginHumanoidDrag(rt: HumanoidLabRuntime, targetBody: BodyHandle, px: number, py: number, pz: number): void {
  endHumanoidDrag(rt);
  if (!rt.handBodyId) {
    const hand = rt.world.createBody({ type: 'kinematic', position: [px, py, pz] });
    rt.world.addSensorBox(hand, [0.05, 0.05, 0.05]);
    rt.handBodyId = hand;
  } else {
    rt.world.setBodyTransform(rt.handBodyId, [px, py, pz], [0, 0, 0, 1]);
  }
  rt.dragJoint = rt.world.createSphericalJoint(rt.handBodyId, targetBody, { anchor: [px, py, pz] });
  rt.handPos = { x: px, y: py, z: pz };
  rt.dragTarget = { x: px, y: py, z: pz };
  rt.dragging = true;
}

function endHumanoidDrag(rt: HumanoidLabRuntime): void {
  if (!rt.dragging && !rt.dragJoint) return;
  if (rt.dragJoint) {
    rt.world.destroyJoint(rt.dragJoint);
    rt.dragJoint = 0;
  }
  if (rt.handBodyId) rt.world.setBodyTransform(rt.handBodyId, [0, -900, 0], [0, 0, 0, 1]);
  rt.dragging = false;
}

function driveHumanoidHand(rt: HumanoidLabRuntime, dt: number): void {
  if (!rt.dragging || !rt.handBodyId) return;
  const dx = rt.dragTarget.x - rt.handPos.x;
  const dy = rt.dragTarget.y - rt.handPos.y;
  const dz = rt.dragTarget.z - rt.handPos.z;
  const d = Math.hypot(dx, dy, dz);
  const maxStep = HAND_MAX_SPEED_MPS * dt;
  const s = d > maxStep ? maxStep / d : 1;
  rt.handPos = { x: rt.handPos.x + dx * s, y: rt.handPos.y + dy * s, z: rt.handPos.z + dz * s };
  rt.world.setKinematicTarget(rt.handBodyId, [rt.handPos.x, rt.handPos.y, rt.handPos.z], [0, 0, 0, 1], dt);
}

/** Scripted drag start: grabs DRAG_BONE at its current position after a short
 *  settle lead-in. Auto-scripted (not pointer-driven) so the scenario is
 *  deterministic for both the demo and E2E — see humanoidLabPhysics's file
 *  comment / the plan deviation note in the final report. */
function startScriptedDrag(rt: HumanoidLabRuntime): void {
  rt.dragStarted = true;
  const humanoid = rt.humanoids[0];
  if (!humanoid) return;
  const offset = rt.buffer.offsetOf(humanoid.bodies[DRAG_BONE]);
  if (offset === undefined) return;
  const t = rt.buffer.transforms;
  beginHumanoidDrag(rt, humanoid.bodies[DRAG_BONE], t[offset], t[offset + 1], t[offset + 2]);
}

function updateScriptedDrag(rt: HumanoidLabRuntime): void {
  if (!rt.dragging) return;
  const elapsedInDragS = rt.scenarioElapsedS - DRAG_LEAD_IN_S;
  const progress = Math.max(0, Math.min(1, elapsedInDragS / DRAG_DURATION_S));
  rt.dragTarget = {
    x: rt.spawnPelvisXZ.x + progress * DRAG_DISTANCE_M,
    y: rt.handPos.y,
    z: rt.spawnPelvisXZ.z,
  };
  if (progress >= 1) endHumanoidDrag(rt);
}

function handleScenarioTimers(rt: HumanoidLabRuntime): void {
  if (rt.scenario === 'hit' && !rt.hitFired && rt.scenarioElapsedS >= HIT_LEAD_IN_S) {
    fireHitImpulse(rt);
  }
  if (rt.scenario === 'drag') {
    if (!rt.dragStarted && rt.scenarioElapsedS >= DRAG_LEAD_IN_S) startScriptedDrag(rt);
    else if (rt.dragStarted) updateScriptedDrag(rt);
  }
}

// --- active control (thin wrapper over humanoidMotor.ts) ---

function pelvisTiltDegOf(buffer: TransformBuffer, hips: BodyHandle): number {
  const offset = buffer.offsetOf(hips);
  if (offset === undefined) return 0;
  const t = buffer.transforms;
  const len = Math.hypot(t[offset + 3], t[offset + 4], t[offset + 5], t[offset + 6]) || 1;
  const rotation: readonly [number, number, number, number] = [
    t[offset + 3] / len,
    t[offset + 4] / len,
    t[offset + 5] / len,
    t[offset + 6] / len,
  ];
  const up = rotateVectorByQuat(rotation, [0, 1, 0]);
  const dot = Math.min(1, Math.max(-1, up[1]));
  return (Math.acos(dot) * 180) / Math.PI;
}

/**
 * The ONE place this file calls into humanoidMotor.ts's assisted active
 * control (pose-hold + stand-assist + get-up). Uses the gate-passing combo
 * from evaluatePoseHold: applyPoseHold + applyStandAssist (marionette chest
 * force + COM-over-feet correction). Do NOT add applyUprightBalance here —
 * its pelvis torque fights the assist over the same DOF and worsens
 * oscillation (measured during poseHold gate tuning). Active in 'balance'
 * (Alive mode) always, and in 'hit' once the scheduled impulse has landed
 * (`rt.hitFired`) — everything before that in 'hit', and every other
 * scenario entirely (including `muscleBalance`), stays off this path.
 *
 * Auto get-up: once knocked down AND settled (not mid-tumble) for
 * KNOCKDOWN_GETUP_DELAY_S, hands off to humanoidMotor.ts's `applyGetUp`
 * until it reports tapered (or MAX_GET_UP_DURATION_S elapses — see that
 * constant's comment for why this file caps it rather than waiting
 * forever), then resumes the normal ramped pose-hold + stand-assist combo.
 *
 * `muscleBalance` is intentionally excluded: its no-cheat path is
 * `updateMuscleBalanceControl` only.
 */
function updateHumanoidActiveControl(rt: HumanoidLabRuntime, dt: number): void {
  const humanoid = rt.humanoids[0];
  const activeScenario = rt.scenario === 'balance' || (rt.scenario === 'hit' && rt.hitFired);
  if (!activeScenario || !humanoid || rt.bodyIds.length === 0) {
    rt.poseHoldActive = false;
    rt.gettingUp = false;
    rt.knockedDownSettledS = 0;
    return;
  }

  if (rt.gettingUp) {
    rt.getUpElapsedS += dt;
    const stillHoisting = applyGetUp(rt.world, humanoid, rt.buffer, rt.getUpState, dt, rt.caps);
    rt.poseHoldActive = true;
    if (!stillHoisting || rt.getUpElapsedS >= MAX_GET_UP_DURATION_S) {
      rt.gettingUp = false;
      rt.poseHoldRamp.strength = 1; // hand off standing, matching applyGetUp's own seamless-taper handoff
      rt.knockedDownSettledS = 0;
    }
    return;
  }

  const tiltDeg = pelvisTiltDegOf(rt.buffer, humanoid.root);
  const knockedDown = tiltDeg > KNOCKDOWN_TILT_DEG;
  if (knockedDown) {
    rt.knockedDownSettledS = rt.settled ? rt.knockedDownSettledS + dt : 0;
    if (rt.knockedDownSettledS >= KNOCKDOWN_GETUP_DELAY_S) {
      rt.gettingUp = true;
      resetGetUpState(rt.getUpState);
      rt.getUpElapsedS = 0;
      rt.knockedDownSettledS = 0;
      rt.poseHoldActive = true;
      return;
    }
  } else {
    rt.knockedDownSettledS = 0;
  }

  const rampedStrength = updatePoseHoldRamp(rt.poseHoldRamp, knockedDown, dt) * rt.poseHoldStrength;
  applyPoseHold(rt.world, humanoid, rt.buffer, STAND_POSE, rampedStrength, rt.caps);
  applyStandAssist(rt.world, humanoid, rt.buffer, rampedStrength, rt.caps);
  rt.poseHoldActive = rampedStrength > 0.01;
}

// --- muscleBalance observation + no-cheat control ---

function readBodyPosition(buffer: TransformBuffer, body: BodyHandle): readonly [number, number, number] {
  const offset = buffer.offsetOf(body);
  if (offset === undefined) return [0, 0, 0];
  const t = buffer.transforms;
  return [t[offset], t[offset + 1], t[offset + 2]];
}

function readBodyQuat(buffer: TransformBuffer, body: BodyHandle): readonly [number, number, number, number] {
  const offset = buffer.offsetOf(body);
  if (offset === undefined) return [0, 0, 0, 1];
  const t = buffer.transforms;
  const len = Math.hypot(t[offset + 3], t[offset + 4], t[offset + 5], t[offset + 6]) || 1;
  return [t[offset + 3] / len, t[offset + 4] / len, t[offset + 5] / len, t[offset + 6] / len];
}

/**
 * Support point for one foot in the muscleBalance path.
 *
 * Default feet: body center (legacy).
 * Anatomical heel-biased feet: shift from foot CoM back toward the ankle by
 * `heelBias` along local −Z so the support target sits on the ankle line
 * inside the actual foot polygon (neutral posture calibration — observation
 * only, no body force / teleport).
 */
function footSupportPoint(
  buffer: TransformBuffer,
  foot: BodyHandle,
  heelBiasM: number,
): readonly [number, number, number] {
  const pos = readBodyPosition(buffer, foot);
  if (heelBiasM <= 0) return pos;
  const q = readBodyQuat(buffer, foot);
  // Local +Z is toe direction at spawn; ankle is heel-side of CoM → −local Z.
  const back = rotateVectorByQuat([q[0], q[1], q[2], q[3]], [0, 0, -heelBiasM]);
  return [pos[0] + back[0], pos[1] + back[1], pos[2] + back[2]];
}

/**
 * Mass-weighted COM + foot support midpoint + pelvis frame from live Box3D
 * transforms / velocities. Used only by the muscleBalance control path.
 */
function buildMuscleObservation(rt: HumanoidLabRuntime, humanoid: Humanoid): MuscleObservation {
  let comX = 0;
  let comY = 0;
  let comZ = 0;
  let comVx = 0;
  let comVy = 0;
  let comVz = 0;
  for (const bone of HUMANOID_BONE_NAMES) {
    const massFraction = HUMANOID_MASS_FRACTIONS[bone];
    const pos = readBodyPosition(rt.buffer, humanoid.bodies[bone]);
    const vel = rt.world.getLinearVelocity(humanoid.bodies[bone]);
    comX += pos[0] * massFraction;
    comY += pos[1] * massFraction;
    comZ += pos[2] * massFraction;
    comVx += vel.x * massFraction;
    comVy += vel.y * massFraction;
    comVz += vel.z * massFraction;
  }

  // Neutral posture calibration: support target on the ankle line inside the
  // foot polygon (foot CoM is heelBias forward of the ankle for anatomical
  // feet). Observation-only — no body force / teleport.
  const heelBiasM =
    humanoid.spec.footLayout === 'anatomical'
      ? anatomicalFootHeelBiasM(humanoid.spec.height, humanoid.spec.proportionPreset)
      : 0;
  const leftSupport = footSupportPoint(rt.buffer, humanoid.bodies.leftFoot, heelBiasM);
  const rightSupport = footSupportPoint(rt.buffer, humanoid.bodies.rightFoot, heelBiasM);
  const supportCenter: readonly [number, number, number] = [
    (leftSupport[0] + rightSupport[0]) * 0.5,
    (leftSupport[1] + rightSupport[1]) * 0.5,
    (leftSupport[2] + rightSupport[2]) * 0.5,
  ];

  const leftFootPosition = readBodyPosition(rt.buffer, humanoid.bodies.leftFoot);
  const rightFootPosition = readBodyPosition(rt.buffer, humanoid.bodies.rightFoot);
  const leftHipPosition = readBodyPosition(rt.buffer, humanoid.bodies.leftUpperLeg);
  const rightHipPosition = readBodyPosition(rt.buffer, humanoid.bodies.rightUpperLeg);
  const leftFootLin = rt.world.getLinearVelocity(humanoid.bodies.leftFoot);
  const rightFootLin = rt.world.getLinearVelocity(humanoid.bodies.rightFoot);
  // Contact truth from begin/end tracker (Gate 0) — never foot height.
  const leftFootGrounded = rt.footContactTracker.isContacting('left');
  const rightFootGrounded = rt.footContactTracker.isContacting('right');
  // Sole pitch about world +X (push 19 foot-slap flatten feedback).
  const leftFootQuat = readBodyQuat(rt.buffer, humanoid.bodies.leftFoot);
  const rightFootQuat = readBodyQuat(rt.buffer, humanoid.bodies.rightFoot);
  const leftFwd = rotateVectorByQuat(
    [leftFootQuat[0], leftFootQuat[1], leftFootQuat[2], leftFootQuat[3]],
    [0, 0, 1],
  );
  const rightFwd = rotateVectorByQuat(
    [rightFootQuat[0], rightFootQuat[1], rightFootQuat[2], rightFootQuat[3]],
    [0, 0, 1],
  );
  const leftFootPitchRad = footSolePitchRadFromForward(leftFwd[1], leftFwd[2]);
  const rightFootPitchRad = footSolePitchRadFromForward(rightFwd[1], rightFwd[2]);

  const pelvisPosition = readBodyPosition(rt.buffer, humanoid.root);
  const pelvisQuat = readBodyQuat(rt.buffer, humanoid.root);
  const pelvisUp = rotateVectorByQuat(
    [pelvisQuat[0], pelvisQuat[1], pelvisQuat[2], pelvisQuat[3]],
    [0, 1, 0],
  );
  const lin = rt.world.getLinearVelocity(humanoid.root);
  const ang = rt.world.getAngularVelocity(humanoid.root);

  return {
    centerOfMass: [comX, comY, comZ],
    supportCenter,
    pelvisPosition,
    pelvisUp,
    pelvisVelocity: [lin.x, lin.y, lin.z],
    pelvisAngularVelocity: [ang.x, ang.y, ang.z],
    centerOfMassVelocity: [comVx, comVy, comVz],
    lastPushDirection: rt.muscleLastPushDirection,
    leftFootPosition,
    rightFootPosition,
    leftFootVelocity: [leftFootLin.x, leftFootLin.y, leftFootLin.z],
    rightFootVelocity: [rightFootLin.x, rightFootLin.y, rightFootLin.z],
    leftHipPosition,
    rightHipPosition,
    leftFootGrounded,
    rightFootGrounded,
    leftFootContactPointCount: rt.footContactLoads.left.pointCount,
    rightFootContactPointCount: rt.footContactLoads.right.pointCount,
    leftFootNormalImpulse: rt.footContactLoads.left.totalNormalImpulse,
    rightFootNormalImpulse: rt.footContactLoads.right.totalNormalImpulse,
    leftFootPitchRad,
    rightFootPitchRad,
  };
}

function resetFootContactLoads(rt: HumanoidLabRuntime): void {
  rt.footContactLoads.left.pointCount = 0;
  rt.footContactLoads.left.totalNormalImpulse = 0;
  rt.footContactLoads.right.pointCount = 0;
  rt.footContactLoads.right.totalNormalImpulse = 0;
  updateFootLoadTelemetry(rt);
}

function updateFootLoadTelemetry(rt: HumanoidLabRuntime): void {
  const leftImpulse = rt.footContactLoads.left.totalNormalImpulse;
  const rightImpulse = rt.footContactLoads.right.totalNormalImpulse;
  const combined = leftImpulse + rightImpulse;
  rt.muscleTelemetry.leftFootContactPointCount = rt.footContactLoads.left.pointCount;
  rt.muscleTelemetry.rightFootContactPointCount = rt.footContactLoads.right.pointCount;
  rt.muscleTelemetry.leftFootNormalImpulse = leftImpulse;
  rt.muscleTelemetry.rightFootNormalImpulse = rightImpulse;
  rt.muscleTelemetry.combinedFootNormalImpulse = combined;
  rt.muscleTelemetry.leftFootLoadFraction = combined > 0 ? leftImpulse / combined : 0;
  rt.muscleTelemetry.rightFootLoadFraction = combined > 0 ? rightImpulse / combined : 0;
}

/**
 * Sample real post-solver manifold impulses. The next control tick consumes
 * this completed step's values, while telemetry/trace can report them now.
 */
function sampleFootContactLoadsAfterStep(rt: HumanoidLabRuntime): void {
  const humanoid = rt.scenario === 'muscleBalance' ? rt.humanoids[0] : undefined;
  if (!humanoid || !rt.caps.contactLoadTelemetry) {
    resetFootContactLoads(rt);
    return;
  }
  rt.world.getBodyContactLoad(humanoid.bodies.leftFoot, rt.footContactLoads.left);
  rt.world.getBodyContactLoad(humanoid.bodies.rightFoot, rt.footContactLoads.right);
  updateFootLoadTelemetry(rt);
}

/**
 * muscleBalance automatic control path.
 *
 * ONLY: observe live transforms → stepMuscleController → applyMuscleJointCommands.
 * NEVER: applyStandAssist, applyUprightBalance, applyPoseHold, applyGetUp,
 * root/chest lift forces, applyForce, applyImpulse, setBodyTransform,
 * setKinematicTarget, or teleport. User pushes live on the separate APIs.
 */
function updateMuscleBalanceControl(rt: HumanoidLabRuntime, dt: number): void {
  if (rt.scenario !== 'muscleBalance') {
    if (rt.muscleTelemetry.controllerActive) {
      rt.muscleTelemetry = { ...rt.muscleTelemetry, controllerActive: false };
    }
    return;
  }

  const humanoid = rt.humanoids[0];
  if (!humanoid || rt.bodyIds.length === 0) {
    rt.muscleTelemetry = { ...rt.muscleTelemetry, controllerActive: false };
    return;
  }

  // Fatigue mode keeps fatigue elevated and disables recovery while active.
  if (rt.muscleFatigueMode && rt.muscleState.fatigue < MUSCLE_FATIGUE_MODE_LEVEL) {
    rt.muscleState.fatigue = MUSCLE_FATIGUE_MODE_LEVEL;
  }

  const observation = buildMuscleObservation(rt, humanoid);
  // Support target for anatomical feet is already ankle-line calibrated in
  // buildMuscleObservation (heel-bias shift inside the foot polygon). Keep
  // controller stanceErrorBias at 0 so we do not double-count the offset.
  const configPartial = {
    strength: rt.poseHoldStrength,
    weakLeftLeg: rt.muscleWeakLeftLeg,
    weakLeftLegCapacity: MUSCLE_WEAK_DEMO_CAPACITY,
    // Measurement-only baseline path; default remains stepEnabled false.
    ...(rt.muscleStepEnabledForMeasurement ? { stepEnabled: true as const } : {}),
    // Evaluation/search-only P1A policy; absent → controller zero baseline.
    ...(rt.muscleP1aSagittalPolicyOffsets
      ? { p1aSagittalPolicyOffsets: rt.muscleP1aSagittalPolicyOffsets }
      : {}),
    ...(rt.muscleFatigueMode
      ? {
          fatigueGainPerS: MUSCLE_FATIGUE_MODE_GAIN_PER_S,
          fatigueRecoveryPerS: 0,
          fatigueTorqueScale: MUSCLE_FATIGUE_DEMO_TORQUE_SCALE,
          // Fatigued recovery is both weaker and delayed/miscoordinated after
          // impact. This reverses only the ankle motor feedback; it never
          // changes body transforms or adds an external assist/force.
          ankleErrorSign: rt.muscleLastPushDirection ? -1 : 1,
          activationRisePerS: 1.2,
          activationFallPerS: 0.6,
        }
      : {}),
  };

  const out = stepMuscleController(
    rt.muscleState,
    observation,
    dt,
    configPartial,
    rt.caps.jointMotors,
  );

  // Measure the push lifecycle from live Box3D state. A push is only marked
  // recovered after a real disturbance was observed and the physical envelope
  // has remained stable for a short dwell; stress falls are reported honestly.
  if (rt.musclePushLifecycle !== 'idle' && (out.phase === 'fallen' || observation.pelvisPosition[1] < 0.65)) {
    // A delayed solver fall still invalidates an earlier provisional recovery.
    rt.musclePushLifecycle = 'fallen';
    rt.musclePushRecoveredDwellS = 0;
  } else if (rt.musclePushLifecycle === 'pending') {
    rt.musclePushElapsedS += dt;
    const pelvisDisplacement = Math.hypot(
      observation.pelvisPosition[0] - rt.spawnPelvisXZ.x,
      observation.pelvisPosition[2] - rt.spawnPelvisXZ.z,
    );
    const horizontalSpeed = Math.hypot(observation.pelvisVelocity[0], observation.pelvisVelocity[2]);
    rt.musclePushPeakSupportErrorM = Math.max(rt.musclePushPeakSupportErrorM, out.supportErrorM);
    rt.musclePushPeakTiltDeg = Math.max(rt.musclePushPeakTiltDeg, out.pelvisTiltDeg);
    rt.musclePushPeakDisplacementM = Math.max(rt.musclePushPeakDisplacementM, pelvisDisplacement);
    rt.musclePushDisturbed ||= out.supportErrorM >= MUSCLE_PUSH_RESPONSE_SUPPORT_M ||
      out.pelvisTiltDeg >= MUSCLE_PUSH_RESPONSE_TILT_DEG ||
      horizontalSpeed >= MUSCLE_PUSH_RESPONSE_SPEED_MPS ||
      pelvisDisplacement >= 0.01;

    const fallen = out.phase === 'fallen' || observation.pelvisPosition[1] < 0.65;
    if (fallen) {
      rt.musclePushLifecycle = 'fallen';
      rt.musclePushRecoveredDwellS = 0;
    } else if (rt.musclePushDisturbed && rt.musclePushElapsedS >= 0.15) {
      const recoveredEnvelope = out.supportErrorM <= MUSCLE_PUSH_RECOVERY_SUPPORT_M &&
        out.pelvisTiltDeg <= MUSCLE_PUSH_RECOVERY_TILT_DEG &&
        observation.pelvisPosition[1] >= 0.8 &&
        horizontalSpeed <= MUSCLE_PUSH_RECOVERY_SPEED_MPS;
      rt.musclePushRecoveredDwellS = recoveredEnvelope ? rt.musclePushRecoveredDwellS + dt : 0;
      if (rt.musclePushRecoveredDwellS >= MUSCLE_PUSH_RECOVERY_DWELL_S) rt.musclePushLifecycle = 'recovered';
    }
  }

  // Joint motors / equal-and-opposite joint torques only — see applyMuscleJointCommands.
  // Records commanded maxTorque + speed/velocity into rt.motorCommands for
  // post-step solver torque readback (Gate A saturation instrumentation).
  applyMuscleJointCommands(
    rt.world,
    humanoid,
    rt.buffer,
    out.jointCommands,
    rt.caps,
    rt.motorCommands,
  );

  const base = telemetryFromMuscleOutput(out, rt.muscleState, true);
  // Preserve runtime-owned push tier telemetry across control steps.
  rt.muscleTelemetry = {
    ...base,
    lastPushDirection: rt.muscleLastPushDirection,
    lastPushTier: rt.muscleLastPushTier,
    lastPushImpulseNs: rt.muscleLastPushImpulseNs,
    pushStressMode: rt.musclePushStressMode,
    pushLifecycle: rt.musclePushLifecycle,
    pushElapsedS: rt.musclePushElapsedS,
    pushPeakSupportErrorM: rt.musclePushPeakSupportErrorM,
    pushPeakTiltDeg: rt.musclePushPeakTiltDeg,
    pushPeakDisplacementM: rt.musclePushPeakDisplacementM,
  };
  // poseHoldActive stays false: this is not the assisted pose-hold path.
  rt.poseHoldActive = false;
  rt.gettingUp = false;
}

// --- settle / awake / pelvis bookkeeping ---

function updateSettle(rt: HumanoidLabRuntime): void {
  let maxLinear = 0;
  let maxAngular = 0;
  for (const body of rt.trackedBodies) {
    const linear = rt.world.getLinearVelocity(body);
    const angular = rt.world.getAngularVelocity(body);
    maxLinear = Math.max(maxLinear, Math.hypot(linear.x, linear.y, linear.z));
    maxAngular = Math.max(maxAngular, Math.hypot(angular.x, angular.y, angular.z));
  }
  const good = maxLinear < SETTLE_LIN_MPS && maxAngular < SETTLE_ANG_RAD_S;
  rt.settleGood = good ? rt.settleGood + 1 : 0;
  rt.settled = rt.settleGood >= SETTLE_SUSTAIN_STEPS;
}

function computeAwake(rt: HumanoidLabRuntime): void {
  if (rt.caps.awakeBodyCount) {
    rt.awakeCount = rt.world.awakeBodyCount();
    return;
  }
  if (!rt.prevValid) {
    rt.awakeCount = rt.bodyIds.length;
    return;
  }
  let awake = 0;
  const eps = 0.0006;
  for (let i = 0; i < rt.bodyIds.length; i += 1) {
    const offset = i * 7;
    const dx = Math.abs(rt.transforms[offset] - rt.prevTransforms[offset]);
    const dy = Math.abs(rt.transforms[offset + 1] - rt.prevTransforms[offset + 1]);
    const dz = Math.abs(rt.transforms[offset + 2] - rt.prevTransforms[offset + 2]);
    if (dx + dy + dz > eps) awake += 1;
  }
  rt.awakeCount = awake;
}

function updatePelvisMetrics(rt: HumanoidLabRuntime): void {
  const humanoid = rt.humanoids[0];
  const offset = humanoid ? rt.buffer.offsetOf(humanoid.root) : undefined;
  if (!humanoid || offset === undefined) {
    rt.pelvisHeight = 0;
    rt.pelvisDisplacementM = 0;
    return;
  }
  const t = rt.buffer.transforms;
  rt.pelvisHeight = t[offset + 1];
  const dx = t[offset] - rt.spawnPelvisXZ.x;
  const dz = t[offset + 2] - rt.spawnPelvisXZ.z;
  rt.pelvisDisplacementM = Math.hypot(dx, dz);
}

// --- frame advance ---

export function advanceHumanoidLabPhysics(rt: HumanoidLabRuntime, delta: number): void {
  let stepMsTotal = 0;
  // Snapshot BEFORE this call's fixed steps (if any) modify `rt.transforms`
  // -- moved here, rather than after `stepper.advance()` returns, so it
  // still captures "state as of the end of the PREVIOUS call" once the
  // per-step buffer refresh below is in the mix. `rt.transforms` cannot
  // change between calls (nothing else writes it), so on a frame where
  // `delta` doesn't accumulate to a full fixed step (no stepping happens),
  // this is a same-value no-op, identical to the original guarded version.
  if (rt.bodyIds.length > 0 && rt.prevValid) rt.prevTransforms.set(rt.transforms);

  const stepped = rt.stepper.advance(delta, () => {
    driveHumanoidHand(rt, FIXED_DT);
    handleScenarioTimers(rt);
    // Assisted Alive/Hit path (pose-hold + stand-assist + get-up). No-op for muscleBalance.
    updateHumanoidActiveControl(rt, FIXED_DT);
    // No-cheat muscle path: joint motors/torques only. No-op outside muscleBalance.
    updateMuscleBalanceControl(rt, FIXED_DT);
    const t0 = performance.now();
    rt.world.step(FIXED_DT, SUBSTEPS);
    stepMsTotal += performance.now() - t0;
    // Gate A: query actual solver motor torque after step using this step's
    // commanded maxTorque / speed (never substitute commanded for actual).
    const muscleHumanoid = rt.scenario === 'muscleBalance' ? rt.humanoids[0] : undefined;
    if (muscleHumanoid && rt.caps.jointMotorTelemetry) {
      rt.motorTelemetry = readHumanoidMotorTelemetry(
        rt.world,
        muscleHumanoid,
        rt.motorCommands,
        true,
      );
    } else {
      rt.motorTelemetry = emptyMotorTelemetrySnapshot();
    }
    sampleFootContactLoadsAfterStep(rt);
    // Gate 0: drain end→begin→hit immediately after step, before game logic
    // / teardown can destroy bodies and before the next control tick.
    ingestFootContactsAfterStep(rt);
    rt.fixedTime += FIXED_DT;
    rt.scenarioElapsedS += FIXED_DT;
    // Refresh the buffer after EVERY internal fixed step, not just once
    // after this whole call's steps finish: `updateHumanoidActiveControl`
    // (and handleScenarioTimers' drag path) read `rt.buffer` to make
    // per-step decisions (tilt-based knockdown/get-up detection). Any frame
    // slower than 60fps batches 2-3 fixed steps into one `advance()` call
    // (FixedStepper's own MAX_STEPS_PER_FRAME cap) -- without this, every
    // step after the first in a batch read STALE, pre-batch transform data,
    // silently masking a fast transient tilt spike. Measured effect: a
    // guaranteed-knockdown impulse that reliably toppled the rig when
    // stepped one-fixed-step-at-a-time (Node, no rendering) never crossed
    // the knockdown tilt threshold at all when driven by a real browser's
    // per-frame delta (E2E, headless Chromium) -- this was the actual root
    // cause, not rAF throttling (verified: `rt.fixedTime` tracked wall-clock
    // almost exactly 1:1 throughout).
    if (rt.bodyIds.length > 0) rt.buffer.readInto(rt.world);
  });
  if (stepped) rt.stepMs = stepMsTotal;

  // Sensor drain remains for drag; contact queues are already drained above
  // on every fixed step. Only drain sensors here when no step ran but drag is
  // active (begin/end/hit were empty without a step).
  if (rt.dragging) rt.world.drainSensorEvents();

  if (rt.bodyIds.length > 0) {
    if (!stepped) rt.buffer.readInto(rt.world); // no steps ran; still reflect the latest world state
    if (stepped) {
      computeAwake(rt);
      updateSettle(rt);
      rt.sleep.sweep(rt.stepper.simTime);
      rt.prevValid = true;
    }
  }

  updatePelvisMetrics(rt);
  rt.bodyCountTotal = rt.caps.bodyCount
    ? rt.world.bodyCount()
    : rt.staticBodyCount + rt.trackedBodies.length + rt.stairBodies.length + (rt.handBodyId ? 1 : 0);
}
